// DataTable.tsx — generische, wiederverwendbare Tabelle im „Refined Classic"-
// Redesign (`.kt` in `.card`). Spalten werden deklarativ als Column<T>[] mit
// eigenem Zell-Renderer beschrieben.
//
// DESIGN: DataTable rendert + verdrahtet Interaktionen anhand deklarativer
// Spalten-Deskriptoren + KONTROLLIERTER Props; der Consumer besitzt Daten,
// Fetch, Filter-Optionen, State und Persistenz. Kein Fetch / kein react-router /
// kein Preference-I/O hier (bleibt generisch für Risiken/Verträge/Schäden …).
// ALLE Erweiterungs-Props sind optional → bestehende Aufrufe bleiben unverändert.
//
// Features (alle opt-in): Sortier-Header (Klick togglet asc/desc/aus), immer
// sichtbares Filter-Icon je Spalte, Spalten verschieben (Drag) + aus-/einblenden
// (Menü) + schmal zusammenklappen, Auswahl-Markierung, Infinite-Scroll-Shell mit
// eigenem Scroll-Container (fixer Header sticky am Container).

import * as React from "react";
import { createPortal } from "../../windowManager/browserFenster/fensterPortal";
import InfiniteScroll from "react-infinite-scroll-component";
import { ColumnsMenu } from "./ColumnsMenu";
import {
  RdColumnFilter,
  type ColumnFilterDescriptor,
} from "./RdColumnFilter";
import { ChevronIcon, PfeilHochIcon, PfeilRunterIcon, SortAufhebenIcon, SortPfeile } from "./tabellenIcons";
import { type DateRangeValue } from "./filterModel";
import { flattenRows } from "./rowModel";
import { alleFenster, aktivesFenster } from "../../windowManager/browserFenster/fensterRegister";

export type SortDirection = "asc" | "desc";
export type SortState = { field: string; direction: SortDirection };

export type Column<T> = {
  /** Stabile Spalten-Id (React-key + interne Referenz, Sortier-/Sicht-Key). */
  id: string;
  /** Inhalt der Kopfzelle (Text oder Node, z.B. Select-all-Checkbox). */
  header: React.ReactNode;
  /** Klartext-Name, wenn `header` kein Text ist (Icon-Kopf): Spaltenmenü, Filter-Tooltip. */
  label?: string;
  /** Renderer der Datenzelle. */
  cell: (row: T, index: number) => React.ReactNode;
  /** Optionale feste Breite (px-Zahl oder CSS-Länge). */
  width?: number | string;
  /** Optionale Mindestbreite (px-Zahl oder CSS-Länge) — wirkt im Auto-Layout
   *  als Untergrenze und als Clamp beim Drag-Resize. */
  minWidth?: number | string;
  /** Per Maus an der rechten Kante in der Breite ziehbar (braucht
   *  columnWidths + onColumnWidthsChange am DataTable). */
  resizable?: boolean;
  /** Zusätzliche Klasse auf th + td (z.B. "colcheck", "colact", "name"). */
  className?: string;
  /** Textausrichtung der Spalte. */
  align?: "left" | "right" | "center";
  /** Klick auf den Header sortiert diese Spalte (braucht onSortChange). */
  sortable?: boolean;
  /** Server-Sortierfeld (Default = id). */
  sortField?: string;
  /** Spaltenfilter (Präsenz → immer sichtbares Filter-Icon). */
  filter?: ColumnFilterDescriptor;
  /** Im Spalten-Menü aus-/einblendbar. */
  hideable?: boolean;
  /** Schmal zusammenklappbar. */
  collapsible?: boolean;
  /** Fest verankert: nicht verschiebbar/ausblendbar (z.B. Auswahl-Spalte). */
  pinned?: boolean;
  /** Beim horizontalen Scrollen links eingefroren (sticky). Frozen-Spalten
   *  müssen führend liegen; alle außer der letzten brauchen eine `width`. */
  frozen?: boolean;
};

/**
 * Ein Abschnitt der Tabelle (opt-in, z. B. die Arbeitsansicht der Kontakte):
 * eine Kopfzeile über die volle Breite, darunter Teile mit optionaler
 * Zwischenzeile. Dieselbe Zeile darf in mehreren Abschnitten stehen — die
 * React-Keys werden je Abschnitt vorangestellt.
 */
export type DataTableAbschnitt<T> = {
  key: string;
  /** `null` = ohne Kopfzeile (z. B. die Suchtreffer vor dem Umfeld-Abschnitt). */
  kopf: React.ReactNode;
  className?: string;
  teile: Array<{ key: string; titel?: React.ReactNode; zeilen: T[] }>;
};

export type DataTableProps<T> = {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T, index: number) => React.Key;
  /** Klick auf die Zeile. Der dritte Parameter liefert das Maus-Event (für
   *  Ctrl-/Shift-Mehrfachauswahl); Callbacks mit zwei Parametern bleiben
   *  unverändert typkompatibel. */
  onRowClick?: (row: T, index: number, event: React.MouseEvent) => void;
  rowSelected?: (row: T, index: number) => boolean;
  /** Rechtsklick auf die Zeile (Zeilen-Kontextmenü). Der Consumer ruft selbst
   *  `preventDefault()`, wenn er das Browser-Menü unterdrücken will. */
  onRowContextMenu?: (row: T, index: number, event: React.MouseEvent) => void;
  /** Zusätzliche Klasse(n) je Zeile — z.B. um einzelne Zeilen als nicht
   *  klickbar zu markieren (`.kt tbody tr` trägt per CSS immer `cursor:pointer`,
   *  ein Inline-Style pro Tabelle reicht dafür nicht). */
  rowClassName?: (row: T, index: number) => string | undefined;
  /** Steuert den Maus-Zeiger je Zeile. Nötig, weil der Zeiger inline gesetzt
   *  wird und damit jede CSS-Klasse schlägt — read-only-Zeilen sähen sonst
   *  klickbar aus, obwohl der Klick nur markiert. Default: alle Zeilen
   *  klickbar, sobald `onRowClick` gesetzt ist. */
  rowClickable?: (row: T, index: number) => boolean;
  /**
   * ZEILEN ZIEHEN (PO 21.09.2026, für „Vorgang auf Vorgang" in der
   * Vorgangsliste). Alle vier Angaben zusammen oder keine.
   *
   * Der Tabelle ist gleichgültig, WAS gezogen wird: `onRowDragStart` legt die
   * Nutzlast selbst in den `dataTransfer` (eigener MIME-Typ, Muster
   * `vorgang/detail/todoDrop.ts`), `rowDropTarget` entscheidet beim Überfahren
   * anhand des Events, ob diese Zeile den Zug annimmt. So bleibt die
   * Entscheidung dort, wo die Fachlichkeit sitzt, und die Tabelle trägt nur
   * die Hervorhebung (`.kt-drop`).
   *
   * Warum nicht dnd-kit (liegt im Projekt): Es überlagert die Zeile mit einem
   * eigenen Sensor-Layer und bräuchte hier Sortier-Kontext, den es nicht gibt.
   * Für „eines auf das andere" reicht HTML5-DnD, und die Datei-Drops der App
   * sprechen ohnehin diese Sprache.
   */
  rowDraggable?: (row: T, index: number) => boolean;
  onRowDragStart?: (row: T, index: number, event: React.DragEvent) => void;
  /** Nimmt diese Zeile den laufenden Zug an? Wird bei jedem `dragover` gefragt. */
  rowDropTarget?: (row: T, index: number, event: React.DragEvent) => boolean;
  onRowDrop?: (row: T, index: number, event: React.DragEvent) => void;
  emptyText?: React.ReactNode;
  card?: boolean;
  cardClassName?: string;
  cardStyle?: React.CSSProperties;
  tableClassName?: string;

  // --- Sortierung (kontrolliert) ---
  sort?: SortState | null;
  onSortChange?: (next: SortState | null) => void;

  // --- Filter (kontrolliert; DataTable rendert Icon+Popover, Consumer hält Werte) ---
  filterValues?: Record<string, string | boolean | undefined>;
  onFilterChange?: (columnId: string, value: string | boolean | undefined) => void;
  boFoValue?: { boTeamIds: string[]; foUserIds: string[] };
  onBoFoChange?: (next: { boTeamIds: string[]; foUserIds: string[] }) => void;
  /** Werte je Spalte für `dateRange`-Filter (opt-in; bestehende Nutzer unberührt). */
  dateRangeValues?: Record<string, DateRangeValue | undefined>;
  onDateRangeChange?: (columnId: string, next: DateRangeValue) => void;
  /** Werte je Spalte für `people`-Filter (gewählte User-Ids, opt-in). */
  peopleValues?: Record<string, string[] | undefined>;
  onPeopleChange?: (columnId: string, next: string[]) => void;

  // --- Spalten: Reihenfolge / Sichtbarkeit / Collapse (kontrolliert) ---
  columnOrder?: string[];
  onColumnOrderChange?: (next: string[]) => void;
  columnVisibility?: Record<string, boolean>;
  onColumnVisibilityChange?: (next: Record<string, boolean>) => void;
  /** Per-Spalte gezogene Breiten (px). Effektive Breite = columnWidths[id] ?? c.width. */
  columnWidths?: Record<string, number>;
  onColumnWidthsChange?: (next: Record<string, number>) => void;
  collapsedColumns?: string[];
  onToggleCollapsed?: (columnId: string) => void;
  /** Spalten-Menü (aus-/einblenden) anzeigen. */
  showColumnMenu?: boolean;
  /**
   * Zusätzliche Einträge unten im Spalten-Kontextmenü (Rechtsklick auf den
   * Spaltenkopf), abgesetzt durch einen Trenner.
   *
   * Sie hängen NICHT an einer einzelnen Spalte, sondern an der Tabelle — daher
   * öffnet das Menü auch dann, wenn die angeklickte Spalte weder sortierbar
   * noch einklappbar ist. Ohne das wäre der Eintrag je nach Spalte mal da und
   * mal nicht, und niemand fände ihn zuverlässig wieder.
   */
  extraMenuItems?: { id: string; label: string; icon?: React.ReactNode; onSelect: () => void }[];

  /**
   * Abschnitte statt einer flachen Liste (opt-in). Gesetzt, rendert der
   * Tabellenkörper nur die Abschnitte; `rows` bleibt für Auswahl, Leer-Zustand
   * und Infinite-Scroll maßgeblich. Ausklappbare Kind-Zeilen gibt es hier nicht.
   */
  abschnitte?: DataTableAbschnitt<T>[];

  // --- Ausklappbare Zeilen (Child-Rows, kontrolliert; opt-in) ---
  /** Liefert die Kind-Zeilen einer (Eltern-)Zeile. Nur gerendert, wenn der
   *  Eltern-Key in `expandedKeys` liegt. */
  rowChildren?: (row: T, index: number) => T[];
  /** Kontrolliert ausgeklappte Zeilen (Keys wie von `rowKey` geliefert). */
  expandedKeys?: Iterable<React.Key>;

  // --- Infinite Scroll ---
  onLoadMore?: () => void;
  hasMore?: boolean;
  loading?: boolean;
  loadingText?: React.ReactNode;

  // --- Scroll-Container (Sticky-Header + State-Restore) ---
  scrollContainerId?: string;
  scrollRef?: React.Ref<HTMLDivElement>;

  /** Zusätzliche Aktionen rechts in der Util-Leiste über der Tabelle. */
  tableActions?: React.ReactNode;
  /** Inhalt INNERHALB des Scroll-Containers, ÜBER der Tabelle — scrollt mit
   *  weg (nur der Sticky-Header bleibt fix). Nur im Infinite-Scroll-Modus. */
  aboveTable?: React.ReactNode;
  /** Inhalt UNTER der Tabelle (im Infinite-Scroll-Modus innerhalb des
   *  Scroll-Containers, nach den Zeilen) — z. B. der Unternehmensverbund
   *  unter einem eindeutigen Suchtreffer. */
  belowTable?: React.ReactNode;
  /**
   * Zeilen VOR den eigentlichen Zeilen, im `<tbody>` (bekommen die
   * Spaltenzahl für `colSpan`) — z. B. die Platzhalter einer laufenden
   * Ablage, die sofort in der Liste stehen sollen. `null` = keine.
   */
  kopfZeilen?: (spalten: number) => React.ReactNode;
};

const ChevronRight = (
  <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 3.5L10.5 8 6 12.5" />
  </svg>
);
const SortCaret = ({ dir }: { dir?: SortDirection }) => (
  <span className={"kt-sort-caret" + (dir ? " on" : "")} data-dir={dir ?? "keine"} aria-hidden>
    <SortPfeile dir={dir} />
  </span>
);

export function DataTable<T>(props: DataTableProps<T>) {
  const {
    columns,
    rows,
    rowKey,
    onRowClick,
    rowSelected,
    onRowContextMenu,
    rowClassName,
    rowClickable,
    rowDraggable,
    onRowDragStart,
    rowDropTarget,
    onRowDrop,
    emptyText = "Keine Einträge vorhanden.",
    card = true,
    cardClassName,
    cardStyle,
    tableClassName,
    sort,
    onSortChange,
    filterValues,
    onFilterChange,
    boFoValue,
    onBoFoChange,
    dateRangeValues,
    onDateRangeChange,
    peopleValues,
    onPeopleChange,
    columnOrder,
    onColumnOrderChange,
    columnVisibility,
    onColumnVisibilityChange,
    columnWidths,
    onColumnWidthsChange,
    collapsedColumns,
    onToggleCollapsed,
    showColumnMenu,
    extraMenuItems,
    onLoadMore,
    hasMore = false,
    loading = false,
    loadingText = "Lade weitere …",
    scrollContainerId = "rd-table-scroll",
    scrollRef,
    tableActions,
    aboveTable,
    belowTable,
    kopfZeilen,
    rowChildren,
    expandedKeys,
    abschnitte,
  } = props;

  const collapsed = React.useMemo(() => new Set(collapsedColumns ?? []), [collapsedColumns]);
  const [dragId, setDragId] = React.useState<string | null>(null);
  const [dropTarget, setDropTarget] = React.useState<{ id: string; pos: "before" | "after" } | null>(null);

  // Effektive Spalten: führende/abschließende pinned bleiben fix, mittlere werden
  // nach columnOrder sortiert; ausgeblendete (nicht-pinned) entfernt.
  const effectiveColumns = React.useMemo(() => {
    const isPinned = (c: Column<T>) => !!c.pinned;
    let lead = 0;
    while (lead < columns.length && isPinned(columns[lead])) lead++;
    let tail = columns.length;
    while (tail > lead && isPinned(columns[tail - 1])) tail--;
    const leading = columns.slice(0, lead);
    const trailing = columns.slice(tail);
    let middle = columns.slice(lead, tail);

    if (columnOrder && columnOrder.length) {
      const byId = new Map(middle.map((c) => [c.id, c]));
      const ordered: Column<T>[] = [];
      for (const id of columnOrder) {
        const c = byId.get(id);
        if (c) { ordered.push(c); byId.delete(id); }
      }
      for (const c of middle) if (byId.has(c.id)) ordered.push(c); // neue Spalten ans Ende
      middle = ordered;
    }
    if (columnVisibility) {
      middle = middle.filter((c) => columnVisibility[c.id] !== false);
    }
    return [...leading, ...middle, ...trailing];
  }, [columns, columnOrder, columnVisibility]);

  const orderableIds = React.useMemo(
    () => effectiveColumns.filter((c) => !c.pinned).map((c) => c.id),
    [effectiveColumns],
  );

  const colStyle = (c: Column<T>): React.CSSProperties | undefined => {
    // Effektive Breite: gezogene Breite hat Vorrang vor der deklarierten width.
    const effWidth = columnWidths?.[c.id] ?? c.width;
    if (effWidth == null && c.minWidth == null && !c.align) return undefined;
    const s: React.CSSProperties = {};
    if (effWidth != null) {
      // Breite autoritativ pinnen (width UND maxWidth): die gezogene Breite wirkt
      // additiv — die Spalte wächst genau um den gezogenen Betrag, andere Spalten
      // schrumpfen NICHT, und die Tabelle (width:max-content) wird breiter
      // (horizontaler Scroll), statt den Platz innerhalb der Tabelle umzuverteilen.
      s.width = effWidth;
      s.maxWidth = effWidth;
    }
    if (c.minWidth != null) s.minWidth = c.minWidth;
    if (c.align) s.textAlign = c.align;
    return s;
  };

  // Eingefrorene (links sticky) Spalten. Die `left`-Offsets werden DYNAMISCH aus
  // den tatsächlichen Header-Zellbreiten gemessen (nicht aus deklarierten Breiten) —
  // sonst werden breitere Inhalte (z.B. „Person" in der Typ-Spalte) von der nächsten
  // frozen Zelle verdeckt/abgeschnitten. Reihenfolge bestimmt nur die letzte Kante.
  const frozenIds = React.useMemo(
    () => effectiveColumns.filter((c) => c.frozen).map((c) => c.id),
    [effectiveColumns],
  );
  const lastFrozenId = frozenIds[frozenIds.length - 1];
  const frozenSet = React.useMemo(() => new Set(frozenIds), [frozenIds]);

  const headRowRef = React.useRef<HTMLTableRowElement>(null);
  const [frozenLefts, setFrozenLefts] = React.useState<Record<string, number>>({});
  React.useLayoutEffect(() => {
    const measure = () => {
      const row = headRowRef.current;
      if (!row) return;
      const ths = Array.from(row.querySelectorAll<HTMLTableCellElement>("th.kt-frozen[data-col]"));
      let run = 0;
      const next: Record<string, number> = {};
      for (const th of ths) {
        const id = th.getAttribute("data-col");
        if (!id) continue;
        next[id] = run;
        run += th.offsetWidth;
      }
      setFrozenLefts((prev) => {
        const keys = Object.keys(next);
        if (keys.length === Object.keys(prev).length && keys.every((k) => prev[k] === next[k])) return prev;
        return next;
      });
    };
    measure();
    alleFenster.addEventListener("resize", measure);
    return () => alleFenster.removeEventListener("resize", measure);
    // columnWidths in den Deps: nach einem Drag-Resize einer frozen Spalte die
    // left-Offsets der nachfolgenden frozen Spalten neu vermessen.
  }, [effectiveColumns, rows.length, columnWidths]);

  const cellStyle = (c: Column<T>, isHeader: boolean): React.CSSProperties | undefined => {
    const base = colStyle(c);
    if (!frozenSet.has(c.id)) return base;
    return { ...(base ?? {}), position: "sticky", left: frozenLefts[c.id] ?? 0, zIndex: isHeader ? 6 : 2 };
  };
  const frozenClass = (c: Column<T>): string => {
    if (!frozenSet.has(c.id)) return "";
    return " kt-frozen" + (c.id === lastFrozenId ? " kt-frozen-last" : "");
  };

  const labelOf = (c: Column<T>) => c.label ?? (typeof c.header === "string" ? c.header : c.id);

  // Spalten-Kontextmenü (Rechtsklick auf den Header): Sortieren + Ein-/Ausklappen.
  const [ctx, setCtx] = React.useState<{ x: number; y: number; col: Column<T> } | null>(null);
  /** Die Zeile unter dem Mauszeiger während eines Zugs — trägt `.kt-drop`. */
  const [dropZeile, setDropZeile] = React.useState<React.Key | null>(null);
  const hasExtraMenu = (extraMenuItems?.length ?? 0) > 0;
  const openCtx = (c: Column<T>) => (e: React.MouseEvent) => {
    if (!hasExtraMenu && !(c.sortable && onSortChange) && !(c.collapsible && onToggleCollapsed)) return;
    e.preventDefault();
    const w = 230;
    const h = 190;
    setCtx({
      x: Math.min(e.clientX, aktivesFenster().innerWidth - w - 8),
      y: Math.min(e.clientY, aktivesFenster().innerHeight - h - 8),
      col: c,
    });
  };
  React.useEffect(() => {
    if (!ctx) return;
    const close = () => setCtx(null);
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") close(); };
    alleFenster.addEventListener("click", close);
    alleFenster.addEventListener("scroll", close, true);
    alleFenster.addEventListener("resize", close);
    alleFenster.addEventListener("keydown", onKey);
    return () => {
      alleFenster.removeEventListener("click", close);
      alleFenster.removeEventListener("scroll", close, true);
      alleFenster.removeEventListener("resize", close);
      alleFenster.removeEventListener("keydown", onKey);
    };
  }, [ctx]);

  // --- Drag-Reorder (nur mittlere, nicht-pinned Spalten) ---
  const canReorder = !!(columnOrder && onColumnOrderChange);
  const onDragStart = (id: string) => (e: React.DragEvent) => {
    const t = e.target as HTMLElement;
    if (t.closest("input,button,select,textarea,label,.kt-filter")) { e.preventDefault(); return; }
    setDragId(id);
    e.dataTransfer.effectAllowed = "move";
  };
  const onDragOver = (id: string) => (e: React.DragEvent) => {
    if (!dragId || dragId === id) return;
    e.preventDefault();
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setDropTarget({ id, pos: e.clientX < r.left + r.width / 2 ? "before" : "after" });
  };
  // Nach einem Drag-Reorder schicken manche Browser noch ein `click` auf die
  // Kopfzelle hinterher. Ohne dieses kurzlebige Flag würde das Verschieben einer
  // Spalte nebenbei ihre Sortierung umschalten.
  const dragJustEnded = React.useRef(false);
  const markDragEnded = () => {
    dragJustEnded.current = true;
    window.setTimeout(() => { dragJustEnded.current = false; }, 250);
  };
  const onDrop = (id: string) => (e: React.DragEvent) => {
    e.preventDefault();
    markDragEnded();
    if (!dragId || !dropTarget || dragId === id || !onColumnOrderChange) { setDragId(null); setDropTarget(null); return; }
    const next = orderableIds.filter((x) => x !== dragId);
    const idx = next.indexOf(dropTarget.id);
    const insertAt = dropTarget.pos === "before" ? idx : idx + 1;
    next.splice(Math.max(0, insertAt), 0, dragId);
    onColumnOrderChange(next);
    setDragId(null);
    setDropTarget(null);
  };
  const onDragEnd = () => { markDragEnded(); setDragId(null); setDropTarget(null); };

  // --- Sortieren per Linksklick auf die Kopfzelle ---
  // Zyklus: nicht sortiert → asc → desc → nicht sortiert. Das Rechtsklick-Menü
  // bietet dieselben Schritte weiterhin explizit an.
  const onHeaderClick = (c: Column<T>) => (e: React.MouseEvent) => {
    if (!onSortChange || dragJustEnded.current) return;
    // Klicks aus Filter-Trichter, Resize-Griff, Ausklapp-Button oder einem
    // beliebigen Bedienelement in der Kopfzelle gehören diesem Element, nicht
    // der Sortierung.
    const t = e.target as HTMLElement;
    if (t.closest("input,button,select,textarea,label,a,.kt-filter,.kt-resize,.kt-expand,.rd-popover")) return;
    const field = c.sortField ?? c.id;
    const dir = sort && sort.field === field ? sort.direction : undefined;
    onSortChange(dir === undefined ? { field, direction: "asc" } : dir === "asc" ? { field, direction: "desc" } : null);
  };

  // --- Drag-Resize (Spaltenbreite an der rechten Header-Kante ziehen) ---
  // Nur aktiv, wenn der Consumer Breiten kontrolliert (columnWidths +
  // onColumnWidthsChange). Effektive Breite = columnWidths[id] ?? c.width.
  const canResize = !!(columnWidths && onColumnWidthsChange);
  const resizeRef = React.useRef<{ id: string; startX: number; startWidth: number; min: number } | null>(null);
  const [resizingId, setResizingId] = React.useState<string | null>(null);

  const toPx = (v: number | string | undefined): number | undefined => {
    if (v == null) return undefined;
    if (typeof v === "number") return v;
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : undefined;
  };

  const onResizePointerDown = (c: Column<T>) => (e: React.PointerEvent) => {
    if (!onColumnWidthsChange) return;
    // Weder Reorder-Drag (draggable th) noch Sort-/Context-Click auslösen.
    e.preventDefault();
    e.stopPropagation();
    const th = (e.currentTarget as HTMLElement).closest("th") as HTMLElement | null;
    const startWidth = columnWidths?.[c.id] ?? toPx(c.width) ?? th?.offsetWidth ?? 120;
    const min = toPx(c.minWidth) ?? 60;
    resizeRef.current = { id: c.id, startX: e.clientX, startWidth, min };
    setResizingId(c.id);
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
  };
  const onResizePointerMove = (e: React.PointerEvent) => {
    const r = resizeRef.current;
    if (!r || !onColumnWidthsChange) return;
    e.preventDefault();
    const next = Math.max(r.min, Math.round(r.startWidth + (e.clientX - r.startX)));
    onColumnWidthsChange({ ...(columnWidths ?? {}), [r.id]: next });
  };
  const endResize = (e: React.PointerEvent) => {
    if (!resizeRef.current) return;
    e.stopPropagation();
    (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
    resizeRef.current = null;
    setResizingId(null);
  };

  const renderHeaderCell = (c: Column<T>) => {
    if (collapsed.has(c.id)) {
      return (
        <th key={c.id} className={"kt-col-collapsed " + (c.className ?? "")} title={labelOf(c)} onContextMenu={openCtx(c)}>
          <button type="button" className="kt-expand" title={`${labelOf(c)} ausklappen`} onClick={() => onToggleCollapsed?.(c.id)}>
            {ChevronRight}
          </button>
        </th>
      );
    }
    const field = c.sortField ?? c.id;
    const sortDir = sort && sort.field === field ? sort.direction : undefined;
    const sortClickable = !!(c.sortable && onSortChange);
    // Während eines aktiven Resize den Reorder-Drag dieser Zelle aussetzen, damit
    // der native dragstart die Pointer-Resize-Geste nicht stört.
    const reorderable = canReorder && !c.pinned && resizingId == null;
    const resizable = canResize && !!c.resizable;
    const dropCls =
      dropTarget?.id === c.id ? (dropTarget.pos === "before" ? " kt-drop-before" : " kt-drop-after") : "";

    return (
      <th
        key={c.id}
        data-col={c.frozen ? c.id : undefined}
        className={(c.className ?? "") + (sortClickable ? " kt-sortable" : "") + (reorderable ? " kt-reorderable" : "") + (dragId === c.id ? " kt-dragging" : "") + (resizingId === c.id ? " kt-resizing" : "") + dropCls + frozenClass(c)}
        style={cellStyle(c, true)}
        draggable={reorderable}
        onDragStart={reorderable ? onDragStart(c.id) : undefined}
        onDragOver={reorderable ? onDragOver(c.id) : undefined}
        onDrop={reorderable ? onDrop(c.id) : undefined}
        onDragEnd={reorderable ? onDragEnd : undefined}
        onClick={sortClickable ? onHeaderClick(c) : undefined}
        onContextMenu={openCtx(c)}
        aria-sort={sortClickable ? (sortDir === "asc" ? "ascending" : sortDir === "desc" ? "descending" : "none") : undefined}
        title={
          sortClickable
            ? `Klick: Sortieren${sortDir === "asc" ? " (jetzt aufsteigend)" : sortDir === "desc" ? " (jetzt absteigend)" : ""} · Rechtsklick: mehr`
            : c.collapsible && onToggleCollapsed
              ? "Rechtsklick: Ein-/Ausklappen"
              : undefined
        }
      >
        <div className="kt-head">
          <span className="kt-head-label">{c.header}</span>
          {/* Der Caret ist die einzige Anzeige, DASS eine Spalte sortierbar ist —
              er erscheint deshalb sofort („↕"), nicht erst nach dem Sortieren. */}
          {c.sortable && (onSortChange || sortDir) ? <SortCaret dir={sortDir} /> : null}
          {c.filter && (onFilterChange || onBoFoChange || onDateRangeChange || onPeopleChange) ? (
            <RdColumnFilter
              label={labelOf(c)}
              descriptor={c.filter}
              value={filterValues?.[c.id]}
              onChange={(v) => onFilterChange?.(c.id, v)}
              boFoValue={boFoValue}
              onBoFoChange={onBoFoChange}
              dateRangeValue={dateRangeValues?.[c.id]}
              onDateRangeChange={onDateRangeChange ? (next) => onDateRangeChange(c.id, next) : undefined}
              peopleValue={peopleValues?.[c.id]}
              onPeopleChange={onPeopleChange ? (next) => onPeopleChange(c.id, next) : undefined}
            />
          ) : null}
        </div>
        {resizable ? (
          // Schmaler Griff an der rechten Kante. stopPropagation + preventDefault
          // halten Reorder-Drag und Sort-/Context-Click sauber raus; draggable=false
          // verhindert, dass der native HTML5-Drag des th startet.
          <span
            className="kt-resize"
            role="separator"
            aria-orientation="vertical"
            aria-label={`Spalte „${labelOf(c)}" Breite ändern`}
            draggable={false}
            onPointerDown={onResizePointerDown(c)}
            onPointerMove={onResizePointerMove}
            onPointerUp={endResize}
            onPointerCancel={endResize}
            onClick={(e) => { e.stopPropagation(); }}
            onDragStart={(e) => { e.preventDefault(); e.stopPropagation(); }}
            onMouseDown={(e) => e.stopPropagation()}
          />
        ) : null}
      </th>
    );
  };

  // Flache Render-Liste inkl. ausgeklappter Kind-Zeilen. Ohne rowChildren/
  // expandedKeys entspricht das exakt den Eltern-Zeilen in Eingangsreihenfolge.
  const flatRows = React.useMemo(
    () => flattenRows(rows, rowKey, { rowChildren, expandedKeys }),
    [rows, rowKey, rowChildren, expandedKeys],
  );

  const renderZeile = (row: T, key: React.Key, index: number, isChild: boolean) => (
    <tr
      key={key}
      className={
        (rowSelected?.(row, index) ? "sel" : "") +
        (isChild ? " kt-childrow" : "") +
        (dropZeile === key ? " kt-drop" : "") +
        (rowClassName?.(row, index) ? " " + rowClassName(row, index) : "")
      }
      draggable={rowDraggable?.(row, index) ?? undefined}
      onDragStart={
        onRowDragStart
          ? (e) => {
              // `stopPropagation` NICHT: Der Zug soll auch dann noch
              // ankommen, wenn eine Zelle ihn selbst mitbekommt.
              onRowDragStart(row, index, e);
            }
          : undefined
      }
      onDragEnd={onRowDragStart ? () => setDropZeile(null) : undefined}
      onDragOver={
        rowDropTarget
          ? (e) => {
              if (!rowDropTarget(row, index, e)) return;
              // Beides nötig: `preventDefault` erlaubt den Drop
              // überhaupt, `dropEffect` macht aus dem Verbotsschild
              // den Bewegen-Zeiger.
              e.preventDefault();
              e.dataTransfer.dropEffect = "move";
              if (dropZeile !== key) setDropZeile(key);
            }
          : undefined
      }
      onDragLeave={
        rowDropTarget
          ? () => setDropZeile((d) => (d === key ? null : d))
          : undefined
      }
      onDrop={
        onRowDrop && rowDropTarget
          ? (e) => {
              setDropZeile(null);
              if (!rowDropTarget(row, index, e)) return;
              e.preventDefault();
              onRowDrop(row, index, e);
            }
          : undefined
      }
      onClick={onRowClick ? (e) => onRowClick(row, index, e) : undefined}
      onContextMenu={
        onRowContextMenu
          ? (e) => {
              // Header-Kontextmenü schließen: ein Rechtsklick löst KEIN
              // `click`-Event aus, dessen Außenklick-Handler würde also
              // nicht greifen — sonst stünden beide Menüs gleichzeitig offen.
              setCtx(null);
              onRowContextMenu(row, index, e);
            }
          : undefined
      }
      style={{
        cursor: onRowClick && (rowClickable?.(row, index) ?? true) ? "pointer" : "default",
      }}
    >
      {effectiveColumns.map((c) =>
        collapsed.has(c.id) ? (
          <td key={c.id} className="kt-col-collapsed" />
        ) : (
          <td key={c.id} className={(c.className ?? "") + frozenClass(c)} style={cellStyle(c, false)}>
            {c.cell(row, index)}
          </td>
        ),
      )}
    </tr>
  );

  const kopf = kopfZeilen?.(effectiveColumns.length) ?? null;
  const table = (
    <table className={"kt" + (tableClassName ? " " + tableClassName : "")}>
      <thead>
        <tr ref={headRowRef}>{effectiveColumns.map(renderHeaderCell)}</tr>
      </thead>
      <tbody>
        {kopf}
        {rows.length === 0 && !loading && !abschnitte && kopf == null ? (
          <tr>
            <td colSpan={effectiveColumns.length}>
              <div className="empty">{emptyText}</div>
            </td>
          </tr>
        ) : (
          abschnitte ? (
            abschnitte.map((a) => (
              <React.Fragment key={"abs:" + a.key}>
                {a.kopf != null ? (
                  <tr className={"kt-sec" + (a.className ? " " + a.className : "")}>
                    <td colSpan={effectiveColumns.length}>{a.kopf}</td>
                  </tr>
                ) : null}
                {a.teile.map((t) => (
                  <React.Fragment key={"teil:" + a.key + ":" + t.key}>
                    {t.titel ? (
                      <tr className="kt-grp">
                        <td colSpan={effectiveColumns.length}>
                          <span>{t.titel}</span>
                        </td>
                      </tr>
                    ) : null}
                    {t.zeilen.map((row, i) => renderZeile(row, `${a.key}:${String(rowKey(row, i))}`, i, false))}
                  </React.Fragment>
                ))}
              </React.Fragment>
            ))
          ) : (
            flatRows.map(({ row, key, index, isChild }) => renderZeile(row, key, index, isChild))
          )
        )}
      </tbody>
    </table>
  );

  const cardWrapped = card ? (
    <div className={"card" + (cardClassName ? " " + cardClassName : "")} style={cardStyle}>
      {table}
    </div>
  ) : (
    table
  );

  const utilBar =
    (showColumnMenu && onColumnVisibilityChange) || tableActions ? (
      <div className="kt-utilbar">
        {tableActions}
        {showColumnMenu && onColumnVisibilityChange ? (
          <ColumnsMenu
            columns={columns}
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={onColumnVisibilityChange}
          />
        ) : null}
      </div>
    ) : null;

  // Spalten-Kontextmenü (Rechtsklick): Sortieren + Ein-/Ausklappen. Per Portal an
  // body, damit es nicht vom Scroll-Container abgeschnitten wird.
  const sortField = ctx ? ctx.col.sortField ?? ctx.col.id : "";
  const ctxMenuPortal =
    ctx && (hasExtraMenu || (ctx.col.sortable && onSortChange) || (ctx.col.collapsible && onToggleCollapsed))
      ? createPortal(
          <div className="rd-ctxmenu" style={{ position: "fixed", top: ctx.y, left: ctx.x, zIndex: 2100 }} onClick={(e) => e.stopPropagation()}>
            {ctx.col.sortable && onSortChange ? (
              <>
                <button type="button" className="rd-ctx-item" onClick={() => { onSortChange({ field: sortField, direction: "asc" }); setCtx(null); }}>
                  <span className="rd-ctx-ic">{PfeilHochIcon}</span>Aufsteigend sortieren
                </button>
                <button type="button" className="rd-ctx-item" onClick={() => { onSortChange({ field: sortField, direction: "desc" }); setCtx(null); }}>
                  <span className="rd-ctx-ic">{PfeilRunterIcon}</span>Absteigend sortieren
                </button>
                {sort && sort.field === sortField ? (
                  <button type="button" className="rd-ctx-item" onClick={() => { onSortChange(null); setCtx(null); }}>
                    <span className="rd-ctx-ic">{SortAufhebenIcon}</span>Sortierung aufheben
                  </button>
                ) : null}
              </>
            ) : null}
            {ctx.col.sortable && onSortChange && ctx.col.collapsible && onToggleCollapsed ? <div className="rd-ctx-sep" /> : null}
            {ctx.col.collapsible && onToggleCollapsed ? (
              <button type="button" className="rd-ctx-item" onClick={() => { onToggleCollapsed(ctx.col.id); setCtx(null); }}>
                <span className="rd-ctx-ic"><ChevronIcon richtung={collapsed.has(ctx.col.id) ? "rechts" : "links"} /></span>
                {collapsed.has(ctx.col.id) ? "Ausklappen" : "Einklappen"}
              </button>
            ) : null}
            {hasExtraMenu && ((ctx.col.sortable && onSortChange) || (ctx.col.collapsible && onToggleCollapsed)) ? (
              <div className="rd-ctx-sep" />
            ) : null}
            {extraMenuItems?.map((it) => (
              <button key={it.id} type="button" className="rd-ctx-item" onClick={() => { it.onSelect(); setCtx(null); }}>
                <span className="rd-ctx-ic">{it.icon ?? ""}</span>
                {it.label}
              </button>
            ))}
          </div>,
          document.body,
        )
      : null;

  // Infinite-Scroll-Variante: eigener Scroll-Container; Tabelle OHNE .card-Wrapper,
  // damit dessen `overflow:hidden` den Sticky-Header (top) nicht bricht. Header +
  // eingefrorene Spalten kleben so direkt am `.tablewrap`-Scroller.
  if (onLoadMore) {
    return (
      <>
        {utilBar}
        <div className="tablewrap" id={scrollContainerId} ref={scrollRef}>
          {aboveTable}
          <InfiniteScroll
            dataLength={rows.length}
            next={onLoadMore}
            hasMore={hasMore}
            loader={<div className="kt-loader">{loadingText}</div>}
            scrollableTarget={scrollContainerId}
            style={{ overflow: "visible" }}
          >
            {table}
          </InfiniteScroll>
          {belowTable}
        </div>
        {ctxMenuPortal}
      </>
    );
  }

  return (
    <>
      {utilBar}
      {cardWrapped}
      {belowTable}
      {ctxMenuPortal}
    </>
  );
}
